import moment from 'moment';
import 'moment/dist/locale/fr.js';

moment.locale('fr');

class Commons {
  static time(time, dateFormat = 'DD MMMM YYYY') {
    if (/^\d+$/.test(time)) time = parseInt(`${time}`);
    return moment(new Date(time)).format(dateFormat);
  }

  static formatNumber(
    value,
    { locale = 'fr-FR', decimals = 0, compact = false, suffix = '' } = {}
  ) {
    if (value == null || isNaN(value)) return '—';

    const formatter = new Intl.NumberFormat(locale, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
      notation: compact ? 'compact' : 'standard',
      compactDisplay: 'short'
    });

    return formatter.format(value) + suffix;
  }

  static isCapacitor() {
    return /capacitor/i.test(window.location.href);
  }

  static copyToClipboard(text, onSuccess = () => {}, onFailed = (err) => {}) {
    // utiliser capacitor si dispo, sinon l'api native
    if (Commons.isCapacitor()) {
      Commons.copyViaCapacitor(text, onSuccess, onFailed);
    } else {
      Commons.copyViaNative(text, onSuccess, onFailed);
    }
  }

  // méthode via capacitor (postMessage)
  static copyViaCapacitor(text, onSuccess, onFailed) {
    const requestId = Commons.generateUUID();
    let timeoutId;

    const responseHandler = (event) => {
      if (
        event.data?.type === 'COPY_RESPONSE' &&
        event.data.requestId === requestId
      ) {
        clearTimeout(timeoutId);
        window.removeEventListener('message', responseHandler);

        if (event.data.success) {
          onSuccess && onSuccess();
        } else {
          onFailed &&
            onFailed(new Error(event.data.error || 'erreur de copie'));
        }
      }
    };

    window.addEventListener('message', responseHandler);

    // timeout de 5 secondes
    timeoutId = setTimeout(() => {
      window.removeEventListener('message', responseHandler);
      onFailed && onFailed(new Error('timeout de copie'));
    }, 5000);

    try {
      window.parent.postMessage(
        {
          type: 'COPY_REQUEST',
          text: text,
          requestId: requestId
        },
        '*'
      );
    } catch (error) {
      window.removeEventListener('message', responseHandler);
      console.log('🚀 ~ Commons ~ copyViaCapacitor ~ error:', error);
      onFailed && onFailed(error);
    }
  }

  // méthode native (clipboard api)
  static copyViaNative(text, onSuccess, onFailed) {
    try {
      const type = 'text/plain';
      const blob = new Blob([text], { type });
      const data = [new ClipboardItem({ [type]: blob })];
      navigator.clipboard.write(data);
      onSuccess && onSuccess();
    } catch (error) {
      console.log('🚀 ~ Commons ~ copyViaNative ~ error:', error);
      onFailed && onFailed(error);
    }
  }

  static setThemeColor(color = '#ffffff') {
    try {
      const themeColorMetaTag = document.querySelector('[name="theme-color"]');
      themeColorMetaTag.setAttribute('content', color);
      if (themeColorMetaTag.getAttribute('content') !== color)
        Commons.setThemeColor(color);
    } catch (error) {
      console.log('🚀 ~ Commons ~ setThemeColor ~ error:', error);
    }
  }

  static async supportNativeShare() {
    if (Commons.isCapacitor()) {
      return new Promise((resolve) => {
        const requestId = Commons.generateUUID();
        const responseHandler = (event) => {
          if (
            event.data?.type === 'SUPPORT_NATIVE_SHARE_RESPONSE' &&
            event.data.requestId === requestId
          ) {
            window.removeEventListener('message', responseHandler);
            resolve(!!event.data.isSupported);
          }
        };

        window.addEventListener('message', responseHandler);
        try {
          window.parent.postMessage(
            { type: 'SUPPORT_NATIVE_SHARE_REQUEST', requestId },
            '*'
          );
        } catch (e) {
          window.removeEventListener('message', responseHandler);
          resolve(false);
        }

        // Timeout fallback
        setTimeout(() => {
          window.removeEventListener('message', responseHandler);
          resolve(false);
        }, 1500);
      });
    }

    // Web standard API
    return typeof navigator.share === 'function';
  }

  static navigatorShare(shareData) {
    return new Promise((resolve, reject) => {
      if (Commons.isCapacitor()) {
        const requestId = Commons.generateUUID();

        const responseHandler = (event) => {
          if (
            event.data?.type === 'NATIVE_SHARE_RESPONSE' &&
            event.data.requestId === requestId
          ) {
            window.removeEventListener('message', responseHandler);

            if (event.data.success) {
              resolve();
            } else {
              reject(new Error(event.data.error || 'Erreur de partage natif'));
            }
          }
        };

        window.addEventListener('message', responseHandler);

        try {
          window.parent.postMessage(
            {
              type: 'NATIVE_SHARE_REQUEST',
              shareData: shareData,
              requestId: requestId
            },
            '*'
          );
        } catch (error) {
          window.removeEventListener('message', responseHandler);
          reject(error);
        }

        // Timeout de 30 secondes pour le partage natif (l'utilisateur peut prendre du temps dans la boîte de dialogue)
        setTimeout(() => {
          window.removeEventListener('message', responseHandler);
          reject(new Error('timeout de partage'));
        }, 30000);
      } else {
        if (navigator.share) {
          navigator.share(shareData).then(resolve).catch(reject);
        } else {
          reject(new Error('Partage natif non supporté'));
        }
      }
    });
  }

  static generateUUID() {
    const buf = new Uint8Array(16);
    crypto.getRandomValues(buf);

    // Version 4
    buf[6] = (buf[6] & 0x0f) | 0x40;
    buf[8] = (buf[8] & 0x3f) | 0x80;

    const hex = [...buf].map((v) => v.toString(16).padStart(2, '0')).join('');

    return (
      hex.slice(0, 8) +
      '-' +
      hex.slice(8, 12) +
      '-' +
      hex.slice(12, 16) +
      '-' +
      hex.slice(16, 20) +
      '-' +
      hex.slice(20)
    );
  }

  static _capacitorRequest(
    type,
    payload,
    expectedResponseType,
    timeout = 3000
  ) {
    return new Promise((resolve) => {
      const requestId = Commons.generateUUID();
      let timeoutId = null;

      const responseHandler = (event) => {
        if (
          event.data?.type === expectedResponseType &&
          event.data.requestId === requestId
        ) {
          clearTimeout(timeoutId);
          window.removeEventListener('message', responseHandler);
          resolve(event.data);
        }
      };

      window.addEventListener('message', responseHandler);

      timeoutId = setTimeout(() => {
        window.removeEventListener('message', responseHandler);
        resolve(null); // timeout → null, jamais d'exception
      }, timeout);

      try {
        window.parent.postMessage({ type, requestId, ...payload }, '*');
      } catch (error) {
        clearTimeout(timeoutId);
        window.removeEventListener('message', responseHandler);
        console.log('🚀 ~ Commons ~ _capacitorRequest ~ error:', error);
        resolve(null);
      }
    });
  }

  // --- Persistance user via Capacitor Preferences ---

  static async syncUserDataToPreferences(userData) {
    if (!Commons.isCapacitor() || !userData) return false;
    const res = await Commons._capacitorRequest(
      'SYNC_USER_DATA',
      { userData },
      'SYNC_USER_DATA_RESPONSE',
      5000
    );
    return !!res?.success;
  }

  static async requestUserDataFromPreferences() {
    if (!Commons.isCapacitor()) return null;
    const res = await Commons._capacitorRequest(
      'REQUEST_USER_DATA',
      {},
      'USER_DATA_RESPONSE',
      3000
    );
    return res?.userData || null;
  }

  static async clearUserDataFromPreferences() {
    if (!Commons.isCapacitor()) return false;
    const res = await Commons._capacitorRequest(
      'CLEAR_USER_DATA',
      {},
      'CLEAR_USER_DATA_RESPONSE',
      3000
    );
    return !!res?.success;
  }
}

export default Commons;
